import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { verifyEmail, resetPassword } from "../api/authApi";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  UserCheck,
  ShoppingBag,
  PackageCheck,
  Check,
  ShieldCheck,
  Lock,
  Globe,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import "./Auth.css";

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { addToast } = useToast();
  const { t, language, isRTL, toggleLanguage } = useLanguage();
  const { themePreference, setThemePreference } = useTheme();

  const [status, setStatus] = useState("loading"); // 'loading', 'success', 'error'
  const [errorKey, setErrorKey] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const [successKey, setSuccessKey] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [isGuestOrder, setIsGuestOrder] = useState(false);

  // Form states for password creation
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [accountCreated, setAccountCreated] = useState(false);

  const hasAttempted = useRef(false);

  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";
  const addressId = searchParams.get("addressId") || searchParams.get("address");

  useEffect(() => {
    // React StrictMode calls useEffect twice in dev, use a ref to prevent double API call
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    if (!email || !token) {
      setStatus("error");
      setErrorKey("verifyEmail.errMissingParams");
      return;
    }

    verifyEmail(email, token, addressId)
      .then((res) => {
        setStatus("success");
        if (res && res.token) {
          // Backend returned password reset token for guest user
          setResetToken(res.token);
          setIsGuestOrder(true);
          setSuccessKey("verifyEmail.guestConfirmedMessage");
        } else if (addressId) {
          setIsGuestOrder(true);
          setSuccessKey("verifyEmail.guestPreparingMessage");
        } else {
          setIsGuestOrder(false);
          setSuccessKey("verifyEmail.standardSuccessMessage");
        }
      })
      .catch((err) => {
        setStatus("error");
        setErrorDetail(err.message || "");
        setErrorKey("verifyEmail.errFailedDefault");
      });
  }, [email, token, addressId]);

  // Password criteria checklist for live feedback
  const passwordChecks = useMemo(() => {
    return {
      length: password.length >= 8 && password.length <= 30,
    };
  }, [password]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!passwordChecks.length) {
      setFormError(t("verifyEmail.errPasswordLength"));
      return;
    }
    if (password !== confirmPassword) {
      setFormError(t("verifyEmail.errPasswordMatch"));
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(email, password, resetToken);
      try {
        await login(email, password);
      } catch (loginErr) {
        console.warn("Auto-login after account creation:", loginErr);
      }
      setAccountCreated(true);
      if (addToast) {
        addToast(t("verifyEmail.toastAccountCreated"), "success");
      }
    } catch (err) {
      setFormError(err.message || t("verifyEmail.errSetPasswordFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const isWide = isGuestOrder && resetToken && !accountCreated;

  return (
    <div className="auth-page">
      <div className={`auth-page-container ${isWide ? "auth-page-container--wide" : ""}`}>
        {/* Quick Language & Theme Controls */}
        <div className="auth-toolbar">
          {/* Language Switcher */}
          <button
            type="button"
            className="auth-util-btn"
            onClick={toggleLanguage}
            title={language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
            aria-label={language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
          >
            <Globe size={14} />
            <span>{language === "ar" ? "English" : "العربية"}</span>
          </button>

          {/* Theme Segmented Switcher (Light / Dark / System) */}
          <div className="auth-theme-group" role="group" aria-label={t("verifyEmail.themeMode")}>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "light" ? "is-active" : ""}`}
              onClick={() => setThemePreference("light")}
              title={t("verifyEmail.themeLight")}
              aria-label={t("verifyEmail.themeLight")}
              aria-pressed={themePreference === "light"}
            >
              <Sun size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeLight")}</span>
            </button>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "dark" ? "is-active" : ""}`}
              onClick={() => setThemePreference("dark")}
              title={t("verifyEmail.themeDark")}
              aria-label={t("verifyEmail.themeDark")}
              aria-pressed={themePreference === "dark"}
            >
              <Moon size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeDark")}</span>
            </button>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "system" ? "is-active" : ""}`}
              onClick={() => setThemePreference("system")}
              title={t("verifyEmail.themeSystem")}
              aria-label={t("verifyEmail.themeSystem")}
              aria-pressed={themePreference === "system"}
            >
              <Monitor size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeSystem")}</span>
            </button>
          </div>
        </div>

        {/* Verification Card */}
        <div
          className={`auth-card ${isWide ? "auth-card--wide" : ""}`}
          style={{ textAlign: "center", padding: "40px 32px" }}
        >
          {/* State: LOADING */}
          {status === "loading" && (
            <>
              <div className="auth-status-icon-wrap auth-status-icon--gold">
                <Loader2 size={48} className="auth-spinner" />
              </div>
              <h2 className="auth-title">{t("verifyEmail.verifyingTitle")}</h2>
              <p className="auth-subtitle">{t("verifyEmail.verifyingMessage")}</p>
            </>
          )}

          {/* State: ERROR */}
          {status === "error" && (
            <>
              <div className="auth-status-icon-wrap auth-status-icon--error">
                <XCircle size={48} />
              </div>
              <h2 className="auth-title">{t("verifyEmail.failedTitle")}</h2>
              <p className="auth-subtitle">
                {errorDetail || t(errorKey || "verifyEmail.errFailedDefault")}
              </p>
              <button
                className="btn-outline"
                style={{ width: "100%", marginTop: "24px" }}
                onClick={() => navigate("/")}
              >
                {t("verifyEmail.returnHome")}
              </button>
            </>
          )}

          {/* State: SUCCESS */}
          {status === "success" && (
            <>
              {/* Sub-case 1: Guest order verified with password creation token */}
              {isGuestOrder && resetToken ? (
                accountCreated ? (
                  /* Account was successfully activated */
                  <div>
                    <div className="auth-status-icon-wrap auth-status-icon--gold">
                      <UserCheck size={52} />
                    </div>
                    <h2 className="auth-title">{t("verifyEmail.accountCreatedTitle")}</h2>
                    <p className="auth-subtitle" style={{ maxWidth: "420px", margin: "0 auto 28px" }}>
                      {t("verifyEmail.accountCreatedIntro")}
                    </p>

                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "340px", margin: "0 auto" }}>
                      <button
                        type="button"
                        className="btn-primary"
                        style={{ width: "100%", justifyContent: "center" }}
                        onClick={() => navigate("/account/orders")}
                      >
                        <PackageCheck size={16} />
                        {t("verifyEmail.viewMyOrders")}
                      </button>
                      <button
                        type="button"
                        className="btn-outline"
                        style={{ width: "100%", justifyContent: "center" }}
                        onClick={() => navigate("/shop")}
                      >
                        <ShoppingBag size={16} />
                        {t("verifyEmail.continueShopping")}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Prompt guest to set password or continue as guest */
                  <div>
                    <div className="auth-status-icon-wrap auth-status-icon--gold" style={{ marginBottom: "16px" }}>
                      <CheckCircle size={48} />
                    </div>

                    <div className="auth-badge-success">
                      <ShieldCheck size={14} />
                      <span>{t("verifyEmail.orderBadgeConfirmed")}</span>
                    </div>

                    <h2 className="auth-title">{t("verifyEmail.orderPlacedTitle")}</h2>
                    <p className="auth-subtitle" style={{ marginBottom: "20px" }}>
                      {t("verifyEmail.guestOrderIntro", { email })}
                    </p>

                    <form onSubmit={handleSetPassword} style={{ textAlign: "start" }}>
                      {formError && (
                        <div className="auth-error" role="alert">
                          {formError}
                        </div>
                      )}

                      <div className="auth-field">
                        <label htmlFor="guest-password">{t("verifyEmail.createPasswordLabel")}</label>
                        <div className="auth-input-wrapper auth-input-wrapper--ltr">
                          <span className="auth-input-icon">
                            <Lock size={16} />
                          </span>
                          <input
                            id="guest-password"
                            type={showPassword ? "text" : "password"}
                            className="has-toggle"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t("verifyEmail.passwordPlaceholder")}
                            autoComplete="new-password"
                            dir="ltr"
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            className="auth-toggle-pwd"
                            aria-label={showPassword ? t("verifyEmail.hidePassword") : t("verifyEmail.showPassword")}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>

                      <div className="auth-field">
                        <label htmlFor="guest-confirm-password">{t("verifyEmail.confirmPasswordLabel")}</label>
                        <div className="auth-input-wrapper auth-input-wrapper--ltr">
                          <span className="auth-input-icon">
                            <Lock size={16} />
                          </span>
                          <input
                            id="guest-confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            className="has-toggle"
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder={t("verifyEmail.confirmPasswordPlaceholder")}
                            autoComplete="new-password"
                            dir="ltr"
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            className="auth-toggle-pwd"
                            aria-label={showConfirmPassword ? t("verifyEmail.hidePassword") : t("verifyEmail.showPassword")}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>

                      {/* Requirements checklist */}
                      <div className="password-requirements">
                        <div className="password-requirements-title">{t("verifyEmail.requirementsTitle")}</div>
                        <div className="password-req-list">
                          <span className={`req-item ${passwordChecks.length ? "met" : ""}`}>
                            <Check size={13} /> {t("verifyEmail.reqLength")}
                          </span>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={submitting}
                        style={{ width: "100%", justifyContent: "center" }}
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={16} className="spinner" style={{ animation: "spin 2s linear infinite" }} />
                            {t("verifyEmail.activatingAccount")}
                          </>
                        ) : (
                          <>
                            <UserCheck size={16} />
                            {t("verifyEmail.setPasswordAndJoin")}
                          </>
                        )}
                      </button>
                    </form>

                    <div className="auth-divider">
                      <span>{t("verifyEmail.or")}</span>
                    </div>

                    <button
                      type="button"
                      className="btn-outline"
                      style={{ width: "100%", justifyContent: "center" }}
                      onClick={() => navigate("/shop")}
                    >
                      <ShoppingBag size={16} />
                      {t("verifyEmail.continueAsGuest")}
                    </button>
                  </div>
                )
              ) : (
                /* Sub-case 2: Standard email verification (e.g. regular registration) */
                <div>
                  <div className="auth-status-icon-wrap auth-status-icon--gold">
                    <CheckCircle size={48} />
                  </div>
                  <h2 className="auth-title">
                    {addressId ? t("verifyEmail.orderConfirmedTitle") : t("verifyEmail.emailVerifiedTitle")}
                  </h2>
                  <p className="auth-subtitle">
                    {t(successKey || "verifyEmail.standardSuccessMessage")}
                  </p>
                  <button
                    className="btn-primary"
                    style={{ width: "100%", marginTop: "24px", justifyContent: "center" }}
                    onClick={() => navigate(addressId ? "/shop" : "/login")}
                  >
                    {addressId ? t("verifyEmail.continueShopping") : t("verifyEmail.goToSignIn")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
